import { AxiosResponse } from 'axios';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import {
  IMessageItem,
  INewsItem,
  IReferralItem,
  IRemoveBalanceForm,
  ITransferItem,
  IUserData,
  MessageTypeEnum,
  TransactionTypeEnum,
  UserRoleEnum,
} from './user.types';
import { HandleTableChange, ITableParams, RootStore } from '../../../stores';
import apiRoutes from '../../../config/api-routes';
import { webSocketService } from '../../../services/web-socket.service';
import { handleCommonTableChange, initTableParams } from '../../../utils/handleCommonTableChange';
import { GameTypeEnum, IGameItem } from '../../games/store/game.types';
import { getImageUrl } from '../../../utils';

const CHAT_PAGE_SIZE = 10;

class UserStore {
  public rootStore: RootStore;
  public data: IUserData | undefined;
  public isAuth = false;
  public getTransfersParams: ITableParams;
  public gamesStatParams: ITableParams;
  public gamesStatList: IGameItem[] = [];
  public gamesStatTotal = 0;
  public gamesTypeStat: GameTypeEnum;
  public totalTransfers = 0;
  public transferList: ITransferItem[] = [];
  public showEmojiPicker = false;
  public isConnectedWS = false;
  public chatMessageList: IMessageItem[] = [];
  public usersOnline = 0;
  public recipient: Pick<IMessageItem, 'recipientId' | 'recipientName'> = {};
  public totalMessages = 0;
  public newsList: INewsItem[] = [];
  public transactionType: TransactionTypeEnum;
  public referralList: IReferralItem[] = [];
  public referralsTotal = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.getTransfersParams = initTableParams;
    this.gamesStatParams = initTableParams;
    this.gamesTypeStat = GameTypeEnum.KNB;
    this.transactionType = TransactionTypeEnum.DEPOSIT;
    makeAutoObservable(this, undefined, { autoBind: true });
  }

  afterCreate() {
    reaction(
      () => this.rootStore.user.isConnectedWS,
      isConnected => {
        if (isConnected) {
          webSocketService.emit('get-chat', { limit: CHAT_PAGE_SIZE });
          webSocketService.on('chat-messages', (e: any) => {
            console.log('WebSocket chat-messages');
            const response = JSON.parse(e);
            console.log(response);
            this.chatMessageList = response.messages;
            this.usersOnline = response.online;
            this.totalMessages = response.total;
          });
        }
      },
    );
  }

  public get isVerivield() {
    return this.isAuth && this.data?.roles?.includes(UserRoleEnum.VERIFIED);
  }

  public get isAdmin() {
    return this.data?.roles?.includes(UserRoleEnum.ADMIN);
  }

  public get isModeratorChat() {
    return this.data?.roles?.includes(UserRoleEnum.CHAT_MODERATOR);
  }

  public async self() {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.self,
      success: (response: AxiosResponse) => {
        const imageUrl = getImageUrl(response.data.userId);
        this.data = { ...this.data, ...response.data, imageUrl };
      },
      errorMessage: 'Get user data failed',
    });
  }

  public async getTransfers() {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.transactionsAllTransfers,
      params: { ...this.getTransfersParams, type: this.transactionType },
      success: (response: AxiosResponse) => {
        this.transferList = response.data.transfers.map((tr: ITransferItem) => ({ ...tr, key: tr.transactionId }));
        this.totalTransfers = response.data.total;
      },
      errorMessage: 'Get users data failed',
    });
  }

  public async getReferrals() {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.referrals,
      params: this.getTransfersParams,
      success: (response: AxiosResponse) => {
        console.log(response.data);

        this.referralList = response.data.referrals.map((tr: IReferralItem) => ({ ...tr, key: tr.login }));
        this.referralsTotal = response.data.total;
      },
      errorMessage: 'Get referals failed',
    });
  }

  public async getAllGames() {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.gamesUser,
      params: { ...this.gamesStatParams, type: this.gamesTypeStat },
      success: (response: AxiosResponse) => {
        this.gamesStatList = response.data.games.map((g: IGameItem) => ({ ...g, key: g.gameId }));
        this.gamesStatTotal = response.data.total;
      },
      errorMessage: 'Get users games failed',
    });
  }

  public async createWitdrawalTransfer(values: IRemoveBalanceForm) {
    await this.rootStore.request({
      method: 'post',
      route: apiRoutes.transactionsBalanceWitdrawal,
      params: values,
      succesCode: 201,
      success: (response: AxiosResponse) => {
        this._successWitdrawalTransfer(response.data.message);
      },
      errorMessage: 'Ошибка создания заявки',
    });
  }

  public async changeWitdrawalTransfer(transactionId: string) {
    await this.rootStore.request({
      method: 'del',
      route: apiRoutes.transactionsBalanceWitdrawal,
      params: { transactionId },
      success: (response: AxiosResponse) => {
        this._successWitdrawalTransfer(response.data.message);
      },
      errorMessage: 'Ошибка отмены заявки',
    });
  }

  public changeTypeStatGames(type: GameTypeEnum) {
    this.gamesTypeStat = type;
    this.gamesStatParams = initTableParams;
    this.getAllGames();
  }

  handleTableChange: HandleTableChange<ITransferItem> = (pagination, filters, sorter, extra) => {
    handleCommonTableChange(pagination, sorter, this.getTransfersParams, () => this.getTransfers());
  };

  handleStatGamesTableChange: HandleTableChange<IGameItem> = (pagination, filters, sorter, extra) => {
    handleCommonTableChange(pagination, sorter, this.gamesStatParams, () => this.getAllGames());
  };

  handleRefTableChange: HandleTableChange<IReferralItem> = (pagination, filters, sorter, extra) => {
    handleCommonTableChange(pagination, sorter, this.getTransfersParams, () => this.getReferrals());
  };

  public chatMessagesLoadMore() {
    const limit = this.chatMessageList.length + CHAT_PAGE_SIZE;
    if (limit >= this.chatMessageList.length) {
      console.log('get more chat');
      webSocketService.emit('get-chat', { limit });
    }
  }

  public getNews = async () => {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.newsHome,
      success: (response: AxiosResponse) => {
        this.newsList = response.data.news;
      },
      errorMessage: 'Get news failed',
    });
  };

  public toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  public setRecipient(e: any) {
    console.log(e);
    runInAction(() => {
      this.recipient = e;
    });
  }

  public setMute(time: number) {
    if (this.recipient.recipientId) {
      this.handleSendMessage(`mute/${time}`);
    }
  }

  public async handleSendMessage(message: string) {
    await this.rootStore.request({
      method: 'post',
      route: apiRoutes.messagesSend,
      params: {
        message,
        type: MessageTypeEnum.CHAT,
        recipientId: this.recipient.recipientId,
      },
      success: (res: AxiosResponse) => {
        this.rootStore.notifications.success(res.data.message);
      },
      errorMessage: 'Ошибка отправки сообщения',
    });

    this.recipient = {};
  }

  connectWebSocket() {
    if (!this.rootStore.user?.isAuth) return;

    webSocketService.connect();

    webSocketService.on('connect', () => {
      this.isConnectedWS = true;
    });

    webSocketService.on('disconnect', () => {
      this.isConnectedWS = false;
    });
  }

  public setAuth(v = false) {
    runInAction(() => {
      this.isAuth = v;
      if (!v) {
        this.data = undefined;
        webSocketService.disconnect();
      } else {
        this.connectWebSocket();
      }
    });
  }

  private _successWitdrawalTransfer(message: string) {
    this.self();
    this.rootStore.game.showCurrentGames();
    this.getTransfers();
    this.rootStore.notifications.success(message);
  }
}

export default UserStore;
